import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { Col, Collapse } from 'antd';
import { clone, getSnapshot, applySnapshot } from 'mobx-state-tree';
import { FormattedMessage, injectIntl } from 'react-intl';
import { startCase, isEqual } from 'lodash';
import CommonButton from '../../components/common/Button';
import UserProfile from '../../components/SettingTabs/UserProfile';
import Subscription from '../../components/SettingTabs/Subscription';
import Notifications from '../../components/SettingTabs/Notifications';
import Pricing from '../../components/SettingTabs/Pricing';
import Listing from '../../components/SettingTabs/Listing';
import Orders from '../../components/SettingTabs/Orders';
import Balance from '../../components/SettingTabs/Balance';
import IconSvg from '../../components/common/IconSvg';
import APIConnect from '../../components/SettingTabs/APIConnect';
import { themeColors } from '../../constants/themes';
import mandatoryFieldsNotCompleted from '../../utils/check-mandatory';
import {
  CustomModal,
  TopRow,
  BotRow,
  ModalHeader,
  ModalConentHeader,
  CustomCard,
  CustomPanel,
  CustomBreadcrumbs,
  ModalCornerButton,
  MainCol,
} from './styled.js';

import ENUMS from '../../constants/enums';
import { listingRequiredFields, userRequiredFields } from '../../constants/settingsRequirements';

const menuBackgroundColor = themeColors.backgroundLightBlue;
const accordionHeaderColor = themeColors.backgroundDarkBlue;

@injectIntl
@inject('settingsStore')
@inject('localeStore')
@inject('toastsStore')
@inject('routingStore')
@observer
class SettingsModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      openKeys: ['sub1'],
      activeTab: props.activeTab || ENUMS.SETTINGS_TABS.USER_PROFILE,
      isFullScreen: false,
      userProfileClone: clone(props.settingsStore.userProfile),
      subscriptionClone: clone(props.settingsStore.subscription),
      notificationsClone: clone(props.settingsStore.notifications),
      listingClone: clone(props.settingsStore.listing),
      ordersClone: clone(props.settingsStore.orders),
      autoReloadClone: clone(props.settingsStore.balance.autoReload),
      breadcrumbs: [],
      applyBtnDisabled: true,
    };
    this.rootSubmenuKeys = ['sub1', 'sub2', 'sub3', 'sub4'];
    this.selectTab = this.selectTab.bind(this);
  }

  onOpenChange = openKeys => {
    const latestOpenKey = openKeys.find(key => this.state.openKeys.indexOf(key) === -1);
    if (this.rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      this.setState({ openKeys });
    } else {
      this.setState({
        openKeys: latestOpenKey ? [latestOpenKey] : [],
      });
    }
  };

  setNotificationChange = (target, bool) => {
    const { notificationsClone } = this.state;
    const path = target.split('.');
    notificationsClone.groups.forEach(group => {
      group.notifications.forEach(inner => {
        if (group.groupId === +path[0] && inner.id === +path[1]) {
          inner.setValue(path[2], bool);
        }
      });
    });
    this.setState({ notificationsClone: this.state.notificationsClone });
  };

  fetchListingOptions = (src, account, dest) => {
    const { listing } = this.props.settingsStore;
    listing.fetchListingOptions(src, account, dest).then(() => {
      this.setState({ listingClone: clone(listing) });
    });
  };

  restoreNotificationsDefaults = () => {
    this.state.notificationsClone.setGroups(this.props.settingsStore.notifications.getDefaults());
    this.setState({ notificationsClone: this.state.notificationsClone });
  };

  selectTab = key => {
    const {
      settingsStore: { updateHash },
    } = this.props;
    const { breadcrumbs } = this.state;
    if (breadcrumbs.length > 1) {
      breadcrumbs[0].clickHandler();
    }
    this.setState(
      {
        applyBtnDisabled: true,
        activeTab: key,
      },
      updateHash(key),
    );
  };

  saveChanges = () => {
    const {
      activeTab,
      userProfileClone,
      listingClone,
      autoReloadClone,
      subscriptionClone,
      ordersClone,
    } = this.state;
    const {
      settingsStore: { userProfile, listing, orders, balance, saveDataToServer, subscription },
      intl: { formatMessage },
      localeStore,
      toastsStore,
      routingStore: { browserHistory },
    } = this.props;
    switch (activeTab) {
      case ENUMS.SETTINGS_TABS.USER_PROFILE: {
        if (mandatoryFieldsNotCompleted(userProfileClone, userRequiredFields)) {
          toastsStore.showToast({
            type: ENUMS.TOAST_TYPES.WARNING,
            message: formatMessage({ id: 'settings.mandatoryNotFilled' }),
          });
        }
        const languageChanged = userProfile.languageId !== userProfileClone.languageId;
        applySnapshot(userProfile, getSnapshot(userProfileClone));
        this.updateApplyBtn();
        saveDataToServer(ENUMS.SETTINGS_TABS.USER_PROFILE);
        if (languageChanged) {
          localeStore.changeLanguage(userProfileClone.languageId);
        }
        if (/\/discover/.test(window.location.pathname)) {
          browserHistory.push('/discover/main#userProfile');
        }
        if (/\/listings\/single-item/.test(window.location.pathname)) {
          browserHistory.push('/listings/table');
        }
        break;
      }
      case ENUMS.SETTINGS_TABS.LISTING: {
        if (mandatoryFieldsNotCompleted(listingClone.listingOptions, listingRequiredFields)) {
          toastsStore.showToast({
            type: ENUMS.TOAST_TYPES.WARNING,
            message: formatMessage({ id: 'settings.mandatoryNotFilled' }),
          });
        }
        applySnapshot(listing, getSnapshot(listingClone));
        this.updateApplyBtn();
        saveDataToServer(ENUMS.SETTINGS_TABS.LISTING);
        applySnapshot(userProfile, getSnapshot(userProfileClone));
        this.updateApplyBtn();
        saveDataToServer(ENUMS.SETTINGS_TABS.USER_PROFILE);
        break;
      }
      case ENUMS.SETTINGS_TABS.ORDERS: {
        applySnapshot(orders, getSnapshot(ordersClone));
        orders.saveOrdersPreferences().then(() => this.updateApplyBtn());
        break;
      }
      case ENUMS.SETTINGS_TABS.BALANCE: {
        const { autoReload } = balance;
        applySnapshot(autoReload, getSnapshot(autoReloadClone));
        balance.saveAutoReload().then(() => this.updateApplyBtn());
        break;
      }
      case ENUMS.SETTINGS_TABS.SUBSCRIPTION: {
        const { subscriptionData, updateAddOns, addOns } = subscription;
        const { subscriptionData: subscriptionDataClone, addOns: addOnsClone } = subscriptionClone;
        const addOnsChanges = addOnsClone.reduce((acc, curr, idx) => {
          if (
            curr.isSelected !== addOns[idx].isSelected ||
            curr.selectedPlanId !== addOns[idx].selectedPlanId
          ) {
            return [
              ...acc,
              {
                typeId: curr.id,
                newAddOnPlanId: curr.selectedPlanId,
              },
            ];
          }
          return acc;
        }, []);
        applySnapshot(subscriptionData, getSnapshot(subscriptionDataClone));
        updateAddOns(addOnsChanges).then(() => this.updateApplyBtn());
        break;
      }
      default: {
        break;
      }
    }
  };

  updateBreadcrumbs = (breadcrumbObj, isReset) => {
    this.setState({
      breadcrumbs: isReset ? [breadcrumbObj] : [...this.state.breadcrumbs, breadcrumbObj],
    });
  };

  updateApplyBtn = disable => {
    if (disable === true || disable === false) {
      this.setState({ applyBtnDisabled: disable });
    } else {
      const { activeTab } = this.state;
      switch (activeTab) {
        case ENUMS.SETTINGS_TABS.USER_PROFILE: {
          const { userProfileClone } = this.state;
          const {
            settingsStore: { userProfile },
          } = this.props;
          const isDisabled = isEqual(getSnapshot(userProfileClone), getSnapshot(userProfile));
          this.setState({
            applyBtnDisabled: isDisabled,
          });
          break;
        }
        case ENUMS.SETTINGS_TABS.LISTING: {
          const { listingClone, userProfileClone } = this.state;
          const {
            settingsStore: { listing, userProfile },
          } = this.props;
          const isDisabled =
            isEqual(getSnapshot(listingClone), getSnapshot(listing)) &&
            getSnapshot(userProfileClone).allowDuplicatesOnRelatedAccounts ===
              getSnapshot(userProfile).allowDuplicatesOnRelatedAccounts;
          this.setState({
            applyBtnDisabled: isDisabled,
          });
          break;
        }
        case ENUMS.SETTINGS_TABS.ORDERS: {
          const {
            settingsStore: { orders },
          } = this.props;
          const { ordersClone } = this.state;
          const isDisabled = isEqual(getSnapshot(ordersClone), getSnapshot(orders));
          this.setState({
            applyBtnDisabled: isDisabled,
          });
          break;
        }
        case ENUMS.SETTINGS_TABS.BALANCE: {
          const { autoReloadClone } = this.state;
          const {
            settingsStore: {
              balance: { autoReload },
            },
          } = this.props;
          const isDisabled = isEqual(getSnapshot(autoReloadClone), getSnapshot(autoReload));
          this.setState({
            applyBtnDisabled: isDisabled,
          });
          break;
        }
        case ENUMS.SETTINGS_TABS.SUBSCRIPTION: {
          const {
            subscriptionClone: { subscriptionData: subscriptionDataClone },
          } = this.state;
          const {
            settingsStore: {
              subscription: { subscriptionData },
            },
          } = this.props;
          const isDisabled = isEqual(
            getSnapshot(subscriptionDataClone),
            getSnapshot(subscriptionData),
          );
          this.setState({
            applyBtnDisabled: isDisabled,
          });
          break;
        }
        default: {
          this.setState({ applyBtnDisabled: false });
        }
      }
    }
  };

  refreshSubscriptionClone = () => {
    const {
      settingsStore: { subscription },
    } = this.props;
    this.setState({
      subscriptionClone: clone(subscription),
      applyBtnDisabled: true,
    });
  };

  renderTab = () => {
    const {
      connectionManagement,
      markets,
      policies,
      subscription: {
        billingPaypalAccountId,
        isPaypalPreferred,
        billingPayoneerAccountId,
        isPayoneerPreferred,
        billingStripeAccountId,
        isStripePreferred,
      },
      listing: { scanUserByToken, loading },
    } = this.props.settingsStore;
    const {
      userProfileClone,
      notificationsClone,
      listingClone,
      autoReloadClone,
      activeTab,
      subscriptionClone,
      ordersClone,
    } = this.state;
    const {
      USER_PROFILE,
      SUBSCRIPTION,
      NOTIFICATIONS,
      PRICING,
      LISTING,
      CONNECT,
      ORDERS,
      BALANCE,
    } = ENUMS.SETTINGS_TABS;
    const userDataIncomplete = mandatoryFieldsNotCompleted(userProfileClone, userRequiredFields);
    switch (activeTab) {
      case USER_PROFILE:
        return (
          <UserProfile
            preventTabChange={() => this.selectTab(USER_PROFILE)}
            updateBreadcrumbs={this.updateBreadcrumbs}
            userProfile={userProfileClone}
            updateApplyBtn={this.updateApplyBtn}
          />
        );
      case SUBSCRIPTION:
        return (
          <Subscription
            updateBreadcrumbs={this.updateBreadcrumbs}
            refreshSubscriptionClone={this.refreshSubscriptionClone}
            selectTab={this.selectTab}
            subscriptionClone={subscriptionClone}
            updateApplyBtn={this.updateApplyBtn}
          />
        );
      case NOTIFICATIONS:
        return (
          <Notifications
            updateBreadcrumbs={this.updateBreadcrumbs}
            setNotificationChange={this.setNotificationChange}
            notificationsData={notificationsClone.getNotificationsGroups}
            restoreDefaults={this.restoreNotificationsDefaults}
          />
        );
      case PRICING:
        return <Pricing updateBreadcrumbs={this.updateBreadcrumbs} />;
      case LISTING:
        return (
          <Listing
            preventTabChange={() => this.selectTab(LISTING)}
            selectTab={this.selectTab}
            updateBreadcrumbs={this.updateBreadcrumbs}
            markets={markets}
            policies={policies}
            editPolicy={policies.editPolicy}
            listingClone={listingClone}
            fetchListingOptions={this.fetchListingOptions}
            listingOptionsLoading={loading}
            scanUserByToken={scanUserByToken}
            updateApplyBtn={this.updateApplyBtn}
            userProfile={userProfileClone}
          />
        );
      case CONNECT:
        return (
          <APIConnect
            connectionManagement={connectionManagement}
            fetchDestinationsOfType={markets.fetchDestinationsOfType}
            billingPaypalAccountId={billingPaypalAccountId}
            isPaypalPreferred={isPaypalPreferred}
            billingPayoneerAccountId={billingPayoneerAccountId}
            isPayoneerPreferred={isPayoneerPreferred}
            billingStripeAccountId={billingStripeAccountId}
            isStripePreferred={isStripePreferred}
            updateBreadcrumbs={this.updateBreadcrumbs}
            selectTab={this.selectTab}
            userDataIncomplete={userDataIncomplete}
            userProfile={userProfileClone}
          />
        );
      case ORDERS:
        return (
          <Orders
            updateApplyBtn={this.updateApplyBtn}
            updateBreadcrumbs={this.updateBreadcrumbs}
            ordersClone={ordersClone}
          />
        );
      case BALANCE:
        return (
          <Balance
            updateBreadcrumbs={this.updateBreadcrumbs}
            updateApplyBtn={this.updateApplyBtn}
            autoReloadClone={autoReloadClone}
          />
        );
      default:
        return 'NO COMP';
    }
  };

  renderBreadcrumbs = () => {
    const { breadcrumbs } = this.state;
    return breadcrumbs.map((crumbObj, i) => (
      <span
        key={crumbObj.name}
        onClick={crumbObj.clickHandler}
        style={{
          cursor: 'pointer',
          color: i === 0 && breadcrumbs.length > 1 ? themeColors.blue : null,
        }}
      >
        {startCase(crumbObj.name)}
        {i !== breadcrumbs.length - 1 && ' > '}
      </span>
    ));
  };

  render() {
    const buttonCss = { width: '128px' };
    const { visible, closeModal } = this.props;
    const {
      USER_PROFILE,
      SUBSCRIPTION,
      NOTIFICATIONS,
      PRICING,
      LISTING,
      ORDERS,
      BALANCE,
      CONNECT,
    } = ENUMS.SETTINGS_TABS;
    const { applyBtnDisabled } = this.state;
    return (
      <CustomModal
        bodyStyle={{ height: '80vh', overflowY: 'hidden', overflowX: 'hidden' }}
        width={this.state.isFullScreen ? '100%' : '60%'}
        visible={visible}
        onCancel={closeModal}
        closable={false}
        footer={[
          <CommonButton key="back" cssProps={buttonCss} clickHandler={closeModal} type="normal">
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </CommonButton>,
          <CommonButton
            key="submit"
            cssProps={buttonCss}
            type="primary"
            clickHandler={this.saveChanges}
            disabled={applyBtnDisabled}
          >
            <FormattedMessage id="settingsModal.apply" defaultMessage="Apply" />
          </CommonButton>,
        ]}
      >
        <TopRow>
          <Col
            span={6}
            style={{ background: menuBackgroundColor, display: 'flex', alignItems: 'center' }}
          >
            <ModalHeader className="mainTitle">
              <FormattedMessage id="settingsModal.settings" defaultMessage="Settings" />
            </ModalHeader>
          </Col>
          <ModalConentHeader>
            <CustomBreadcrumbs>{this.renderBreadcrumbs()}</CustomBreadcrumbs>
            <div>
              <ModalCornerButton
                onClick={() => this.setState({ isFullScreen: !this.state.isFullScreen })}
              >
                <IconSvg
                  hoverColor={themeColors.blue}
                  size={10}
                  type={this.state.isFullScreen ? 'shrink' : 'expand'}
                />
              </ModalCornerButton>
              <ModalCornerButton onClick={closeModal}>
                <IconSvg size={10} type="exit" hoverColor={themeColors.blue} />
              </ModalCornerButton>
            </div>
          </ModalConentHeader>
        </TopRow>
        <BotRow>
          <Col
            span={6}
            style={{
              padding: '0',
              height: '100%',
              background: menuBackgroundColor,
              overflow: 'auto',
            }}
          >
            <Collapse
              accordion
              bordered={false}
              style={{ backgroundColor: accordionHeaderColor, borderRadius: '0' }}
            >
              <CustomPanel
                key="sub1"
                header={
                  <FormattedMessage
                    id="settingsModal.profileSettings"
                    defaultMessage="Profile Settings"
                  />
                }
              >
                <CustomCard
                  key="1"
                  onClick={() => this.selectTab(USER_PROFILE)}
                  active={this.state.activeTab === USER_PROFILE ? 'true' : undefined}
                >
                  <FormattedMessage id="settingsModal.userProfile" defaultMessage="User Profile" />
                </CustomCard>
                <CustomCard
                  key="2"
                  onClick={() => this.selectTab(SUBSCRIPTION)}
                  active={this.state.activeTab === SUBSCRIPTION ? 'true' : undefined}
                >
                  <FormattedMessage id="settingsModal.subscription" defaultMessage="Subscription" />
                </CustomCard>
              </CustomPanel>
              <CustomPanel
                key="sub3"
                header={
                  <FormattedMessage
                    id="settingsModal.systemPreferences"
                    defaultMessage="System Preferences"
                  />
                }
              >
                <CustomCard
                  key="5"
                  onClick={() => this.selectTab(NOTIFICATIONS)}
                  active={this.state.activeTab === NOTIFICATIONS ? 'true' : undefined}
                >
                  <FormattedMessage
                    id="settingsModal.notifications"
                    defaultMessage="Notifications"
                  />
                </CustomCard>
                <CustomCard
                  key="6"
                  onClick={() => this.selectTab(PRICING)}
                  active={this.state.activeTab === PRICING ? 'true' : undefined}
                >
                  <FormattedMessage id="settingsModal.pricing" defaultMessage="Pricing" />
                </CustomCard>
                <CustomCard
                  key="7"
                  onClick={() => this.selectTab(LISTING)}
                  active={this.state.activeTab === LISTING ? 'true' : undefined}
                >
                  <FormattedMessage id="settingsModal.listing" defaultMessage="Listing" />
                </CustomCard>
                <CustomCard
                  key="8"
                  onClick={() => this.selectTab(ORDERS)}
                  active={this.state.activeTab === ORDERS ? 'true' : undefined}
                >
                  <FormattedMessage id="common.orders" defaultMessage="Orders" />
                </CustomCard>
                <CustomCard
                  key="9"
                  onClick={() => this.selectTab(BALANCE)}
                  active={this.state.activeTab === BALANCE ? 'true' : undefined}
                >
                  <FormattedMessage id="settingsModal.balance" defaultMessage="Finance" />
                </CustomCard>
              </CustomPanel>
              <CustomPanel
                key="sub4"
                header={
                  <FormattedMessage
                    id="settingsModal.connectionManagement"
                    defaultMessage="Connection Management (API)"
                  />
                }
              >
                <CustomCard
                  key="10"
                  onClick={() => this.selectTab(CONNECT)}
                  active={this.state.activeTab === CONNECT ? 'true' : undefined}
                >
                  <FormattedMessage id="settingsModal.connect" defaultMessage="Connect" />
                </CustomCard>
              </CustomPanel>
            </Collapse>
          </Col>
          <MainCol
            id="tm-settings-modal-content"
            className="scroll-1"
            style={{ overflowY: 'scroll', height: '87%' }}
            span={18}
          >
            {this.renderTab()}
          </MainCol>
        </BotRow>
      </CustomModal>
    );
  }
}

SettingsModal.propTypes = {
  settingsStore: PropTypes.object,
  toastsStore: PropTypes.object,
  localeStore: PropTypes.object,
  intl: PropTypes.object,
  visible: PropTypes.bool,
  closeModal: PropTypes.func,
  activeTab: PropTypes.number,
};

export default SettingsModal;
